import { join, dirname } from 'path';
import { homedir } from 'os';
import { spawnSync } from 'child_process';
import fs from 'fs-extra';
import { logger } from './logger.js';
import { planUpdateRemovals, isNodeModulesPath } from './update-removals.js';
import {
  configureClaudeEnvironment,
  configureStatusLine,
  configureClaudeMd,
  configureSupportRulesMd,
  configureGitGuardHook,
  configureEfGuardHook,
  configureBaAuditGuardHook,
  configureSkillsGuardHook,
  removeMcpServerRegistration,
} from './vscode-settings.js';
import { sweepStatusLineOrphans } from './statusline-sweep.js';

const PACKAGE_ROOT = join(__dirname, '..');
let TEMPLATES_DIR = join(PACKAGE_ROOT, 'templates');
const INSTALL_DIRS = ['agents', 'hooks', 'skills', 'scripts'];
const MANIFEST_FILE = '.smartstack-manifest.json';

/**
 * Provision the skill CLIs' runtime dependencies in the deployed skills dir.
 *
 * Every skill CLI is executed via `npx tsx skills/.../index.ts`. If tsx (and the
 * directly-imported zod / minimatch) are not installed locally, npx fetches them
 * from the registry on first use — a cold download of tsx + esbuild that, on
 * Windows (Defender scanning the native binary), can take minutes. That was the
 * root cause of "`/gitflow init` hangs for 5 minutes before doing anything".
 *
 * Running `npm install` once here (deps declared in templates/skills/package.json)
 * makes the CLIs resolve from a local node_modules and warms the npm cache, so the
 * first — and every — CLI call is instant. Best-effort: a failure only degrades to
 * the previous on-demand-npx behaviour.
 */
async function provisionSkillRuntime(skillsDir: string): Promise<void> {
  if (!(await fs.pathExists(join(skillsDir, 'package.json')))) return;

  const spinner = logger.spinner('Provisioning skill runtime (tsx, zod, minimatch)…');
  try {
    const res = spawnSync('npm install --no-audit --no-fund --loglevel=error', {
      cwd: skillsDir,
      stdio: 'ignore',
      shell: true,
      timeout: 600_000,
    });
    spinner.stop();
    if (res.status === 0) {
      logger.success('Skill runtime ready (tsx provisioned — no cold npx download)');
    } else {
      logger.warning(
        `Skill runtime not pre-installed (npm exit ${res.status ?? 'n/a'}). ` +
          `First CLI call may be slow. Fix: cd "${skillsDir}" && npm install`,
      );
    }
  } catch (error) {
    spinner.stop();
    logger.warning(
      `Skill runtime provisioning skipped: ${error instanceof Error ? error.message : 'unknown'}. ` +
        `Fix: cd "${skillsDir}" && npm install`,
    );
  }
}

/**
 * Override the templates source directory.
 * Used by `ss install --dev` to read from the local development directory
 * instead of the globally installed npm package.
 */
export function setTemplatesDir(dir: string): void {
  TEMPLATES_DIR = dir;
}

/**
 * Get the current templates source directory (for logging/diagnostics).
 */
export function getTemplatesDir(): string {
  return TEMPLATES_DIR;
}

/**
 * Detect if a path is a SmartStack CLI development directory.
 * Returns the templates path if valid, null otherwise.
 */
export function detectDevTemplates(devPath: string): string | null {
  const templatesPath = join(devPath, 'templates');
  const packagePath = join(devPath, 'package.json');

  try {
    if (!fs.existsSync(packagePath) || !fs.existsSync(templatesPath)) {
      return null;
    }

    const pkg = JSON.parse(fs.readFileSync(packagePath, 'utf-8'));
    if (pkg.name === '@atlashub/smartstack-cli') {
      // Verify templates/skills exists
      if (fs.existsSync(join(templatesPath, 'skills'))) {
        return templatesPath;
      }
    }
  } catch {
    // Not a valid dev directory
  }
  return null;
}

// ============================================================================
// Manifest Management - Track installed files for clean updates
// ============================================================================

export interface ManifestEntry {
  path: string;
  hash?: string;
  installedAt: string;
}

export interface Manifest {
  version: string;
  installedAt: string;
  updatedAt: string;
  files: ManifestEntry[];
}

async function readManifest(claudeDir: string): Promise<Manifest | null> {
  const manifestPath = join(claudeDir, MANIFEST_FILE);
  try {
    if (await fs.pathExists(manifestPath)) {
      const content = await fs.readFile(manifestPath, 'utf-8');
      return JSON.parse(content) as Manifest;
    }
  } catch {
    // Manifest corrupt or unreadable - will be recreated
  }
  return null;
}

async function writeManifest(claudeDir: string, manifest: Manifest): Promise<void> {
  const manifestPath = join(claudeDir, MANIFEST_FILE);
  await fs.writeFile(manifestPath, JSON.stringify(manifest, null, 2), 'utf-8');
}

function getPackageVersion(): string {
  try {
    const packagePath = join(PACKAGE_ROOT, 'package.json');
    const pkg = JSON.parse(fs.readFileSync(packagePath, 'utf-8'));
    return pkg.version || '0.0.0';
  } catch {
    return '0.0.0';
  }
}

function processTemplateVariables(content: string, version: string): string {
  const sanitized = version.replace(/[^0-9a-zA-Z.\-+]/g, '');
  return content.replace(/\{\{SMARTSTACK_VERSION\}\}/g, sanitized);
}

// Default plugins to install from claude-plugins-official marketplace
const DEFAULT_PLUGINS: { name: string; marketplace: string }[] = [];

export interface InstallOptions {
  force?: boolean;
  global?: boolean;
  skipConfig?: boolean;
  components?: ('commands' | 'agents' | 'hooks' | 'skills' | 'all')[];
  /**
   * Whether to overwrite a user's *existing custom* status line with the
   * SmartStack one. Defaults to `force`, so only an explicit `ss install --force`
   * replaces it. Routine updates (`ss update`) pass `false` to never clobber a
   * status line the user set themselves. (The SmartStack status line we already
   * own is always kept up to date regardless.)
   */
  forceStatusLine?: boolean;
}

export interface UninstallOptions {
  global?: boolean;
  keepConfig?: boolean;
  components?: ('commands' | 'agents' | 'hooks' | 'skills' | 'all')[];
}

function getClaudeDir(global: boolean): string {
  if (global) {
    return join(homedir(), '.claude');
  } else {
    return join(process.cwd(), '.claude');
  }
}

async function getTemplateFiles(dir: string, baseDir: string = ''): Promise<string[]> {
  const files: string[] = [];
  const entries = await fs.readdir(dir, { withFileTypes: true });

  for (const entry of entries) {
    const relativePath = baseDir ? join(baseDir, entry.name) : entry.name;

    if (entry.isDirectory()) {
      const subFiles = await getTemplateFiles(join(dir, entry.name), relativePath);
      files.push(...subFiles);
    } else if (entry.isFile() && !entry.name.startsWith('.')) {
      files.push(relativePath);
    }
  }

  return files;
}

/**
 * Claude Code only registers a skill when its directory is a DIRECT child of the
 * skills root (the folder name is the slash-command token; nested folders are not
 * invocable and there is no namespacing for plain skills). The BA skills are
 * authored grouped under `business-analyse/<name>/` for repo organisation, so at
 * deploy time we FLATTEN + PREFIX them to `ba-<name>/` — each phase then becomes
 * an individual, grouped command (`/ba-create-menu`, `/ba-create-actors`, …).
 *
 * Returns the remapped relative path, or `null` for files that must NOT be
 * deployed as commands (the `_workflow/` companions and the `CLAUDE.md` index —
 * these stay repo-only; each skill inlines what it needs).
 */
export function remapSkillPath(file: string): string | null {
  const norm = file.replace(/\\/g, '/');
  const GROUP = 'business-analyse/';
  if (!norm.startsWith(GROUP)) {
    return file; // top-level skills (incl. ba-develop) deploy as-is
  }
  const rest = norm.slice(GROUP.length); // e.g. 'create-menu/SKILL.md' | 'CLAUDE.md' | '_workflow/ba-files.md'
  const slash = rest.indexOf('/');
  if (slash === -1) {
    return null; // bare file directly under business-analyse/ (e.g. CLAUDE.md index) — not deployed
  }
  const name = rest.slice(0, slash);
  if (name.startsWith('_')) {
    return null; // internal companions/helpers (_workflow, _fallback-summary) — repo-only
  }
  return `ba-${rest}`; // business-analyse/create-menu/SKILL.md → ba-create-menu/SKILL.md
}

/**
 * The BA flatten (business-analyse/<name>/ → ba-<name>/, see remapSkillPath)
 * removes exactly ONE directory level. The shared lib stays at skills/lib/, so a
 * CLI authored 4 levels deep (business-analyse/<name>/cli/<cli>/) imports it with
 * `../../../../lib/` — correct from the source tree (and exercised by the source
 * test suite), but one `../` too many once the file is deployed 3 levels deep at
 * ba-<name>/cli/<cli>/, where it over-climbs past skills/ to ~/.claude/lib/ and
 * fails to resolve at launch. The byte-for-byte copy never adjusted it.
 *
 * This re-anchors the two reference kinds the flatten invalidates, applied ONLY
 * to flattened BA files at deploy time (the source stays untouched and green):
 *   1. relative imports that climb OUT of the skill to another top-level
 *      skills/ tree — drop a single leading `../`. Covers the shared lib
 *      (`../../../../lib/output.js` → `../../../lib/output.js`, incl.
 *      `export … from` re-exports) AND the development/ tree (a BA CLI
 *      reusing a shared parser, e.g. derive-uc-coverage →
 *      scaffold-tests-from-ac/parse-ac.js; before this the deployed import
 *      over-climbed to ~/.claude/development/ and the CLI died at launch).
 *   2. documented/commented CLI invocation paths — re-point at the deployed
 *      location (`skills/business-analyse/<name>/cli/…` → `skills/ba-<name>/cli/…`),
 *      including cross-skill references such as ba-loop → reconcile-menu.
 *
 * Pure + deterministic: always run once over pristine source content, never
 * stacked on an already-rewritten file (the installer re-reads source each run).
 */
/**
 * The deployed layout has NO `skills/business-analyse/` directory — the
 * installer flattens it to `skills/ba-*`. ANY deployed file citing a
 * cross-skill CLI path in the source-tree form (a development/* SKILL
 * invoking a BA CLI, a ba-develop reference doc) must be re-pointed, or the
 * documented invocation 404s at runtime — until 2026-08 only flattened BA
 * files got this rewrite, so e.g. audit-dev-core's DEV-CORE-011 invocation
 * shipped pointing at a path that does not exist. References to the SOURCE
 * repo (`templates/skills/business-analyse/…`) are left intact — they
 * intentionally point at the repo (and `_workflow/` is never deployed).
 */
export function rewriteDeployedSkillPaths(content: string): string {
  return content.replace(/(?<!templates\/)skills\/business-analyse\//g, 'skills/ba-');
}

export function rewriteFlattenedSkillContent(content: string): string {
  return rewriteDeployedSkillPaths(
    content
      .replace(/(?:\.\.\/){2,}(?:lib|development)\//g, (match) => match.slice(3)) // remove one '../'
      // Cross-BA-skill relative imports (audit-ba → create-rbac/create-screen
      // derive engines): the climb count is UNCHANGED — in the source, N `../`
      // reach the `business-analyse/` container; deployed, the same N reach
      // the skills root where the flattened sibling lives — only the target
      // folder gains the `ba-` prefix. ENUMERATED targets only — never a
      // generic pattern (a folder that is not a deployed skill must keep
      // failing loudly).
      .replace(/((?:\.\.\/){2,})(create-rbac|create-screen)\//g, (_m, dots: string, name: string) => `${dots}ba-${name}/`),
  );
}

/**
 * Legacy paths that previous SmartStack CLI versions (v4.x) installed
 * but that are removed/renamed in v5. Purged only when no manifest exists
 * (fresh upgrade from a version that did not write a manifest).
 */
const LEGACY_PATHS: Record<string, string[]> = {
  skills: [
    // Renamed in v5 (underscore prefix removed)
    '_efcore', '_gitflow', '_lib', '_external',
    // Removed in v5 (replaced by per-step skills or by the new ctx7 CLI / removed altogether)
    'mcp', 'apex', 'apex-verify',
    'business-analyse-handoff',
    'controller', 'review-code', 'validate',
    'workflow', 'notification', 'application', 'sketch',
    'debug', 'explore', 'refactor', 'feature-full',
    'audit-route', 'cc-agent', 'cc-audit', 'cc-skill',
    'migrate', '_resources', '_shared.md',
    'ai-prompt',
    // v5 BA migration: skills moved from nested business-analyse/<name>/ to
    // flattened, prefixed ba-<name>/ (Claude Code only registers direct children
    // of the skills root). Purge the old nested group on upgrade.
    'business-analyse',
    // v5: business-analyse-develop renamed to ba-develop (ba- prefix grouping).
    'business-analyse-develop',
    // Removed in v5.16: the Ralph loop was absorbed by /ba-develop.
    'ralph-loop',
  ],
  agents: [
    // Renamed (moved under skills/<name>/agents/)
    'efcore', 'gitflow',
    // Removed in v5
    'mcp-healthcheck.md', 'ba-reader.md', 'ba-writer.md',
  ],
  hooks: ['mcp-check.md', 'ralph-mcp-logger.sh', 'ralph-session-end.sh', 'stop-hook.sh'],
  scripts: ['setup-ralph-loop.sh'],
  commands: [],
};

/**
 * Remove only the files tracked in the previous install manifest, scoped to
 * one top-level directory (`skills`, `agents`, ...). Files the user added
 * themselves are preserved.
 */
async function removeManifestedFiles(
  claudeDir: string,
  manifest: Manifest,
  scopeDir: string,
): Promise<number> {
  let removed = 0;
  const matches = (p: string) =>
    p === scopeDir || p.startsWith(`${scopeDir}/`) || p.startsWith(`${scopeDir}\\`);

  for (const entry of manifest.files) {
    if (!matches(entry.path)) continue;
    const fullPath = join(claudeDir, entry.path);
    if (await fs.pathExists(fullPath)) {
      await fs.remove(fullPath);
      removed++;
    }
  }
  return removed;
}

/**
 * Purge known legacy SmartStack v4.x paths that are removed or renamed in v5.
 * Only called when no manifest is present (first upgrade onto v5).
 */
async function purgeLegacyPaths(targetDir: string, scopeDir: string): Promise<number> {
  const legacyItems = LEGACY_PATHS[scopeDir] || [];
  let purged = 0;
  for (const item of legacyItems) {
    const fullPath = join(targetDir, item);
    if (await fs.pathExists(fullPath)) {
      await fs.remove(fullPath);
      purged++;
    }
  }
  return purged;
}

/**
 * Recursively remove empty subdirectories (does NOT remove the root itself).
 */
async function removeEmptySubdirs(root: string): Promise<void> {
  if (!(await fs.pathExists(root))) return;
  const entries = await fs.readdir(root, { withFileTypes: true });
  for (const entry of entries) {
    if (!entry.isDirectory()) continue;
    const subPath = join(root, entry.name);
    await removeEmptySubdirs(subPath);
    const remaining = await fs.readdir(subPath);
    if (remaining.length === 0) {
      await fs.rmdir(subPath);
    }
  }
}

export async function installCommands(options: InstallOptions = {}): Promise<{
  installed: number;
  skipped: number;
  errors: string[];
  installedFiles: string[];
}> {
  const global = options.global ?? true;
  const claudeDir = getClaudeDir(global);
  const components = options.components ?? ['all'];
  const installAll = components.includes('all');

  const result = {
    installed: 0,
    skipped: 0,
    errors: [] as string[],
    installedFiles: [] as string[],
  };

  logger.info(`Installing to: ${claudeDir}`);

  const dirsToInstall = installAll
    ? INSTALL_DIRS
    : INSTALL_DIRS.filter((d) => components.includes(d as 'commands' | 'agents' | 'hooks' | 'skills'));

  const version = getPackageVersion();

  // Auto-detect version upgrade: force update when CLI version differs from installed version
  let forceUpdate = options.force ?? false;
  let versionUpgrade = false;
  if (!forceUpdate) {
    const existingManifest = await readManifest(claudeDir);
    if (existingManifest && existingManifest.version !== version) {
      logger.info(`Version upgrade detected: ${existingManifest.version} → ${version} (forcing update)`);
      forceUpdate = true;
      versionUpgrade = true;
    }
  }

  // v5 migration: remove legacy MCP server entries when upgrading from v4.x or on --force
  if (forceUpdate) {
    try {
      const mcpCleanup = await removeMcpServerRegistration();
      for (const path of mcpCleanup.cleaned) {
        logger.success(`Removed legacy MCP entries from ${path}`);
      }
      for (const err of mcpCleanup.errors) {
        logger.warning(`MCP cleanup: ${err}`);
      }
      if (mcpCleanup.cleaned.length === 0 && mcpCleanup.errors.length === 0 && versionUpgrade) {
        logger.debug('No legacy MCP server entries found');
      }
    } catch (error) {
      logger.warning(`MCP cleanup failed: ${error instanceof Error ? error.message : 'unknown'}`);
    }
  }

  for (const dir of dirsToInstall) {
    const sourceDir = join(TEMPLATES_DIR, dir);
    const targetDir = join(claudeDir, dir);

    if (!(await fs.pathExists(sourceDir))) {
      logger.warning(`Source directory not found: ${dir}`);
      continue;
    }

    if (forceUpdate && await fs.pathExists(targetDir)) {
      // Manifest-based deletion: only remove files SmartStack previously installed.
      // User-added skills/agents/hooks at the same level are preserved.
      const existingManifest = await readManifest(claudeDir);
      let removedCount = 0;
      if (existingManifest) {
        removedCount = await removeManifestedFiles(claudeDir, existingManifest, dir);
        logger.info(`Cleaned ${removedCount} previously-installed ${dir} file(s) (user files preserved)`);
      } else {
        // Pre-v5 install with no manifest — purge known legacy paths only
        removedCount = await purgeLegacyPaths(targetDir, dir);
        if (removedCount > 0) {
          logger.info(`Cleaned ${removedCount} legacy ${dir} item(s) from pre-v5 install`);
        }
      }
      // Remove now-empty subdirectories left behind, but keep the root
      await removeEmptySubdirs(targetDir);
    }

    // A dev checkout (`ss install --dev`, npm link) carries templates/skills/node_modules;
    // the published package never does. The runtime is provisioned by npm below,
    // never copied — so a dev install deploys, and manifests, exactly what a client gets.
    const files = (await getTemplateFiles(sourceDir)).filter((f) => !isNodeModulesPath(f));

    for (const file of files) {
      const src = join(sourceDir, file);

      // Skills are flattened + prefixed at deploy (business-analyse/<name>/ →
      // ba-<name>/) so each BA phase is invocable as /ba-<name>. Other dirs
      // (agents, hooks, scripts) deploy verbatim.
      const deployFile = dir === 'skills' ? remapSkillPath(file) : file;
      if (deployFile === null) {
        continue; // internal companion / index — not deployed as a command
      }

      const dest = join(targetDir, deployFile);
      const relativePath = join(dir, deployFile);

      try {
        const exists = await fs.pathExists(dest);

        if (exists && !forceUpdate) {
          result.skipped++;
          result.installedFiles.push(relativePath); // Track even skipped files
          continue;
        }

        await fs.ensureDir(dirname(dest));

        // Flattened BA skills lose one directory level on deploy, which breaks
        // their relative skills/lib/ imports and the CLI paths their docs cite.
        // Re-anchor those (rewriteFlattenedSkillContent); every other file —
        // top-level skills, nested development/*, binaries — deploys verbatim.
        const norm = file.replace(/\\/g, '/');
        const isFlattenedBa = dir === 'skills' && norm.startsWith('business-analyse/');
        const isCode = /\.(ts|tsx|mts|cts|js|mjs|cjs|jsx)$/.test(norm);

        // For .md files, inject template variables (e.g. {{SMARTSTACK_VERSION}}) atomically
        if (file.endsWith('.md')) {
          const content = await fs.readFile(src, 'utf-8');
          let processed = processTemplateVariables(content, version);
          // Flattened BA files get the full re-anchor; every OTHER deployed
          // file still needs its cross-skill `skills/business-analyse/…`
          // citations re-pointed at the flattened `skills/ba-*` location.
          processed = isFlattenedBa
            ? rewriteFlattenedSkillContent(processed)
            : rewriteDeployedSkillPaths(processed);
          await fs.writeFile(dest, processed, 'utf-8');
        } else if (isFlattenedBa && isCode) {
          const content = await fs.readFile(src, 'utf-8');
          await fs.writeFile(dest, rewriteFlattenedSkillContent(content), 'utf-8');
        } else if (isCode) {
          // Non-BA code deploys byte-for-byte UNLESS it cites a BA skill path
          // (comments documenting a cross-skill invocation) — then re-point it.
          const content = await fs.readFile(src, 'utf-8');
          const rewritten = rewriteDeployedSkillPaths(content);
          if (rewritten !== content) {
            await fs.writeFile(dest, rewritten, 'utf-8');
          } else {
            await fs.copy(src, dest, { overwrite: forceUpdate });
          }
        } else {
          await fs.copy(src, dest, { overwrite: forceUpdate });
        }

        result.installed++;
        result.installedFiles.push(relativePath);

        if (deployFile.endsWith('.md') && !deployFile.includes('/') && !deployFile.includes('\\')) {
          logger.success(`Installed ${dir}/${deployFile}`);
        }
      } catch (error) {
        const msg = `Failed to install ${file}: ${error}`;
        result.errors.push(msg);
        logger.error(msg);
      }
    }

    const dirFiles = files.length;
    logger.success(`Installed ${dir}/ (${dirFiles} files)`);
  }

  // Provision the skill runtime so `npx tsx` resolves locally (no cold download).
  if (installAll || components.includes('skills')) {
    await provisionSkillRuntime(join(claudeDir, 'skills'));
  }

  if (installAll || components.includes('commands')) {
    const gitflowDir = join(claudeDir, 'gitflow');
    await fs.ensureDir(join(gitflowDir, 'plans'));
    await fs.ensureDir(join(gitflowDir, 'logs'));
    await fs.ensureDir(join(gitflowDir, 'migrations'));
    await fs.ensureDir(join(gitflowDir, 'backup'));
    await fs.ensureDir(join(gitflowDir, 'cache'));

    if (!options.skipConfig) {
      const configSrc = join(PACKAGE_ROOT, 'config', 'default-config.json');
      const configDest = join(gitflowDir, 'config.json');

      if (!(await fs.pathExists(configDest)) || options.force) {
        if (await fs.pathExists(configSrc)) {
          await fs.copy(configSrc, configDest);
          logger.success('Created gitflow/config.json');
        }
      }
    }
  }

  // Configure Claude Code environment variables
  try {
    const envResult = await configureClaudeEnvironment();
    if (envResult.success) {
      logger.success('Configured Claude Code environment (MAX_OUTPUT_TOKENS=64000)');
    } else {
      logger.warning(`Failed to configure Claude environment: ${envResult.error}`);
    }
  } catch {
    logger.warning('Claude environment configuration skipped');
  }

  // Configure the SmartStack status line — only on a full install, where the
  // scripts/ directory (and thus the status line script) was actually deployed.
  if (installAll) {
    try {
      // Replace an existing *custom* status line only on an explicit --force;
      // routine updates keep the user's own one (forceStatusLine === false).
      const forceStatusLine = options.forceStatusLine ?? options.force ?? false;
      const statusResult = await configureStatusLine(claudeDir, { force: forceStatusLine });
      switch (statusResult.status) {
        case 'set':
          logger.success('Configured SmartStack status line');
          break;
        case 'unchanged':
          logger.debug('SmartStack status line already configured');
          break;
        case 'kept-foreign':
          logger.info('Existing custom status line left unchanged (use --force to replace with SmartStack)');
          break;
        case 'missing-script':
          logger.warning('Status line script not found after deploy — status line skipped');
          break;
        case 'error':
          logger.warning(`Status line not configured: ${statusResult.error}`);
          break;
      }
    } catch (error) {
      logger.warning(`Status line configuration skipped: ${error instanceof Error ? error.message : 'unknown'}`);
    }

    // Windows: reap the status line processes frozen at creation (parent killed
    // by Claude Code's abort before ResumeThread — see statusline-sweep.ts). The
    // deployed script sweeps them every 5 min from now on; this clears the
    // backlog accumulated before this version, which would otherwise linger.
    if (process.platform === 'win32') {
      try {
        const sweep = await sweepStatusLineOrphans();
        if (sweep.killed.length > 0) {
          logger.success(`Purged ${sweep.killed.length} frozen status line process(es)`);
        } else if (sweep.error) {
          logger.debug(`Status line sweep skipped: ${sweep.error}`);
        } else {
          logger.debug('No frozen status line process');
        }
      } catch (error) {
        logger.debug(`Status line sweep skipped: ${error instanceof Error ? error.message : 'unknown'}`);
      }
    }
  }

  // SmartStack git-safety guardrails: the managed CLAUDE.md rules block (soft —
  // tells Claude the rule) + the git-guard PreToolUse hook (hard — blocks any
  // unapproved git write with exit 2). Both deploy on a full install and on every
  // `ss upgrade` (which routes through installCommands).
  if (installAll) {
    try {
      const mdResult = await configureClaudeMd(claudeDir, { force: options.force });
      switch (mdResult.status) {
        case 'created':
          logger.success('Wrote SmartStack Git rules into CLAUDE.md');
          break;
        case 'updated':
          logger.success('Updated SmartStack Git rules in CLAUDE.md (previous file backed up)');
          break;
        case 'unchanged':
          logger.debug('SmartStack Git rules already up to date in CLAUDE.md');
          break;
        case 'error':
          logger.warning(`CLAUDE.md rules not written: ${mdResult.error}`);
          break;
      }
    } catch (error) {
      logger.warning(`CLAUDE.md rules configuration skipped: ${error instanceof Error ? error.message : 'unknown'}`);
    }

    try {
      const supportMd = await configureSupportRulesMd(claudeDir, { force: options.force });
      switch (supportMd.status) {
        case 'created':
          logger.success('Wrote SmartStack CLI Support rules into CLAUDE.md');
          break;
        case 'updated':
          logger.success('Updated SmartStack CLI Support rules in CLAUDE.md (previous file backed up)');
          break;
        case 'unchanged':
          logger.debug('SmartStack CLI Support rules already up to date in CLAUDE.md');
          break;
        case 'error':
          logger.warning(`CLAUDE.md CLI Support rules not written: ${supportMd.error}`);
          break;
      }
    } catch (error) {
      logger.warning(`CLAUDE.md CLI Support rules configuration skipped: ${error instanceof Error ? error.message : 'unknown'}`);
    }

    try {
      const guardResult = await configureGitGuardHook(claudeDir, { force: options.force });
      switch (guardResult.status) {
        case 'set':
          logger.success('Registered git-guard PreToolUse hook (blocks unapproved git writes)');
          break;
        case 'unchanged':
          logger.debug('git-guard hook already registered');
          break;
        case 'missing-script':
          logger.warning('git-guard hook script not deployed — hook not registered');
          break;
        case 'error':
          logger.warning(`git-guard hook not registered: ${guardResult.error}`);
          break;
      }
    } catch (error) {
      logger.warning(`git-guard hook configuration skipped: ${error instanceof Error ? error.message : 'unknown'}`);
    }

    try {
      const efGuard = await configureEfGuardHook(claudeDir, { force: options.force });
      switch (efGuard.status) {
        case 'set':
          logger.success('Registered ef-guard PreToolUse hook (blocks raw dotnet ef writes — use /efcore)');
          break;
        case 'unchanged':
          logger.debug('ef-guard hook already registered');
          break;
        case 'missing-script':
          logger.warning('ef-guard hook script not deployed — hook not registered');
          break;
        case 'error':
          logger.warning(`ef-guard hook not registered: ${efGuard.error}`);
          break;
      }
    } catch (error) {
      logger.warning(`ef-guard hook configuration skipped: ${error instanceof Error ? error.message : 'unknown'}`);
    }

    try {
      const baGuard = await configureBaAuditGuardHook(claudeDir, { force: options.force });
      switch (baGuard.status) {
        case 'set':
          logger.success('Registered ba-audit-guard PreToolUse hook (blocks manual BA-audit subagents — use /ba-audit-run)');
          break;
        case 'unchanged':
          logger.debug('ba-audit-guard hook already registered');
          break;
        case 'missing-script':
          logger.warning('ba-audit-guard hook script not deployed — hook not registered');
          break;
        case 'error':
          logger.warning(`ba-audit-guard hook not registered: ${baGuard.error}`);
          break;
      }
    } catch (error) {
      logger.warning(`ba-audit-guard hook configuration skipped: ${error instanceof Error ? error.message : 'unknown'}`);
    }

    try {
      const skillsGuard = await configureSkillsGuardHook(claudeDir, { force: options.force });
      switch (skillsGuard.status) {
        case 'set':
          logger.success('Registered skills-guard PreToolUse hook (blocks edits to deployed ~/.claude files — use /support-report)');
          break;
        case 'unchanged':
          logger.debug('skills-guard hook already registered');
          break;
        case 'missing-script':
          logger.warning('skills-guard hook script not deployed — hook not registered');
          break;
        case 'error':
          logger.warning(`skills-guard hook not registered: ${skillsGuard.error}`);
          break;
      }
    } catch (error) {
      logger.warning(`skills-guard hook configuration skipped: ${error instanceof Error ? error.message : 'unknown'}`);
    }
  }

  // Write manifest with all installed files
  const now = new Date().toISOString();
  const manifest: Manifest = {
    version: getPackageVersion(),
    installedAt: now,
    updatedAt: now,
    files: result.installedFiles.map((path) => ({
      path,
      installedAt: now,
    })),
  };
  await writeManifest(claudeDir, manifest);

  return result;
}

export async function uninstallCommands(options: UninstallOptions = {}): Promise<{
  removed: number;
  errors: string[];
}> {
  const global = options.global ?? true;
  const claudeDir = getClaudeDir(global);
  const components = options.components ?? ['all'];
  const removeAll = components.includes('all');

  const result = {
    removed: 0,
    errors: [] as string[],
  };

  const dirsToRemove = removeAll
    ? INSTALL_DIRS
    : INSTALL_DIRS.filter((d) => components.includes(d as 'commands' | 'agents' | 'hooks' | 'skills'));

  for (const dir of dirsToRemove) {
    const targetDir = join(claudeDir, dir);

    if (await fs.pathExists(targetDir)) {
      try {
        if (dir === 'commands') {
          const ourDirs = [
            'gitflow', 'gitflow.md', 'efcore', 'efcore.md',
            'prompts', 'utils', '_resources', 'epct.md', 'debug.md',
            'explore.md', 'explain.md', 'oneshot.md', 'quick-search.md',
            'review.md', 'implement.md',
          ];

          for (const item of ourDirs) {
            const itemPath = join(targetDir, item);
            if (await fs.pathExists(itemPath)) {
              await fs.remove(itemPath);
              result.removed++;
            }
          }
          logger.success(`Removed commands (SmartStack files only)`);
        } else {
          await fs.remove(targetDir);
          result.removed++;
          logger.success(`Removed ${dir}/`);
        }
      } catch (error) {
        const msg = `Failed to remove ${dir}: ${error}`;
        result.errors.push(msg);
        logger.error(msg);
      }
    }
  }

  if (!options.keepConfig && (removeAll || components.includes('commands'))) {
    const gitflowDir = join(claudeDir, 'gitflow');
    if (await fs.pathExists(gitflowDir)) {
      await fs.remove(gitflowDir);
      logger.success('Removed gitflow/');
      result.removed++;
    }
  }

  // Best-effort: deregister the git-guard hook from settings.json and strip the
  // managed Git-rules block from CLAUDE.md (the hook file itself was removed with
  // the hooks/ dir above). User content outside the managed markers is preserved.
  if (removeAll || components.includes('hooks')) {
    try {
      const guard = await configureGitGuardHook(claudeDir, { remove: true });
      if (guard.status === 'removed') logger.success('Deregistered git-guard PreToolUse hook');
      const efGuard = await configureEfGuardHook(claudeDir, { remove: true });
      if (efGuard.status === 'removed') logger.success('Deregistered ef-guard PreToolUse hook');
      const baGuard = await configureBaAuditGuardHook(claudeDir, { remove: true });
      if (baGuard.status === 'removed') logger.success('Deregistered ba-audit-guard PreToolUse hook');
      const skillsGuard = await configureSkillsGuardHook(claudeDir, { remove: true });
      if (skillsGuard.status === 'removed') logger.success('Deregistered skills-guard PreToolUse hook');
      const md = await configureClaudeMd(claudeDir, { remove: true });
      if (md.status === 'removed') logger.success('Removed SmartStack Git rules block from CLAUDE.md');
      const supportMd = await configureSupportRulesMd(claudeDir, { remove: true });
      if (supportMd.status === 'removed') logger.success('Removed SmartStack CLI Support block from CLAUDE.md');
    } catch {
      /* best-effort cleanup — never fails the uninstall */
    }
  }

  return result;
}

export interface UpdateOptions {
  global?: boolean;
  dryRun?: boolean;
}

export interface UpdateResult {
  installed: number;
  updated: number;
  removed: number;
  removedFiles: string[];
  errors: string[];
}

export async function updateCommands(options: UpdateOptions = {}): Promise<UpdateResult> {
  const global = options.global ?? true;
  const claudeDir = getClaudeDir(global);
  const dryRun = options.dryRun ?? false;

  const result: UpdateResult = {
    installed: 0,
    updated: 0,
    removed: 0,
    removedFiles: [],
    errors: [],
  };

  // Read existing manifest to know what was previously installed
  const oldManifest = await readManifest(claudeDir);
  const oldFiles = oldManifest?.files.map((f) => f.path) ?? [];

  if (dryRun) {
    logger.info('DRY RUN - No files will be modified');

    // The manifest the redeploy WOULD write — same walk as installCommands.
    const newFiles: string[] = [];
    for (const dir of INSTALL_DIRS) {
      const sourceDir = join(TEMPLATES_DIR, dir);
      if (!(await fs.pathExists(sourceDir))) continue;
      for (const file of await getTemplateFiles(sourceDir)) {
        if (isNodeModulesPath(file)) continue;
        const deployFile = dir === 'skills' ? remapSkillPath(file) : file;
        if (deployFile === null) continue;
        newFiles.push(join(dir, deployFile));
      }
    }

    const plan = planUpdateRemovals({
      oldManifest: oldFiles,
      newManifest: newFiles,
      onDisk: await listDeployedFiles(claudeDir),
    });
    const wouldRemove = [...plan.obsolete, ...plan.orphans, ...plan.staleRuntimeDirs.map((d) => `${d}/`)];

    if (wouldRemove.length > 0) {
      logger.info(`\nFiles that would be REMOVED (${wouldRemove.length}):`);
      for (const file of wouldRemove) {
        logger.info(`  - ${file}`);
        result.removedFiles.push(file);
      }
    } else {
      logger.info('No obsolete files to remove');
    }

    result.removed = wouldRemove.length;
    return result;
  }

  // Install new files (this also creates new manifest)
  const installResult = await installCommands({
    force: true,
    forceStatusLine: false, // never clobber a user's own status line on a routine update
    skipConfig: true,
    global,
  });
  result.installed = installResult.installed;
  result.updated = installResult.installed;

  const newManifest = await readManifest(claudeDir);
  const plan = planUpdateRemovals({
    oldManifest: oldFiles,
    newManifest: newManifest?.files.map((f) => f.path) ?? [],
    onDisk: await listDeployedFiles(claudeDir),
  });

  // Stale leftovers of a previous version are routine housekeeping (info), not
  // warnings. Anything outside what SmartStack ships — the skill runtime,
  // Claude Code's skills/synced/, the user's own skills — is not in the plan.
  const removals: { path: string; label: string }[] = [
    ...plan.obsolete.map((path) => ({ path, label: 'obsolete' })),
    ...plan.orphans.map((path) => ({ path, label: 'orphan' })),
    ...plan.staleRuntimeDirs.map((path) => ({ path, label: 'stale runtime' })),
  ];
  for (const { path, label } of removals) {
    const filePath = join(claudeDir, path);
    try {
      if (await fs.pathExists(filePath)) {
        await fs.remove(filePath);
        result.removed++;
        result.removedFiles.push(path);
        logger.info(`Removed ${label}: ${path}`);
      }
    } catch (error) {
      const msg = `Failed to remove ${path}: ${error}`;
      result.errors.push(msg);
      logger.error(msg);
    }
  }

  // Clean up empty directories
  await cleanEmptyDirectories(claudeDir);

  return result;
}

/** Every file currently deployed under the install dirs, relative to `claudeDir`. */
async function listDeployedFiles(claudeDir: string): Promise<string[]> {
  const files: string[] = [];
  for (const dir of INSTALL_DIRS) {
    const targetDir = join(claudeDir, dir);
    if (!(await fs.pathExists(targetDir))) continue;
    for (const file of await getTemplateFiles(targetDir)) {
      files.push(join(dir, file));
    }
  }
  return files;
}

/**
 * Remove empty directories after file cleanup
 */
async function cleanEmptyDirectories(dir: string): Promise<void> {
  for (const subDir of INSTALL_DIRS) {
    const targetDir = join(dir, subDir);
    if (await fs.pathExists(targetDir)) {
      await removeEmptyDirs(targetDir);
    }
  }
}

async function removeEmptyDirs(dir: string): Promise<boolean> {
  try {
    const entries = await fs.readdir(dir, { withFileTypes: true });

    // Recursively clean subdirectories first
    for (const entry of entries) {
      if (entry.isDirectory()) {
        await removeEmptyDirs(join(dir, entry.name));
      }
    }

    // Re-read after potential subdirectory removal
    const remaining = await fs.readdir(dir);
    if (remaining.length === 0) {
      await fs.rmdir(dir);
      return true;
    }
  } catch {
    // Ignore errors during cleanup
  }
  return false;
}

export async function checkInstallation(options: { global?: boolean } = {}): Promise<{
  location: string;
  installed: boolean;
  components: {
    commands: { installed: boolean; count: number };
    agents: { installed: boolean; count: number };
    hooks: { installed: boolean; count: number };
    skills: { installed: boolean; count: number };
  };
  gitflow: {
    configExists: boolean;
    plansCount: number;
  };
}> {
  const global = options.global ?? true;
  const claudeDir = getClaudeDir(global);

  const result = {
    location: claudeDir,
    installed: false,
    components: {
      commands: { installed: false, count: 0 },
      agents: { installed: false, count: 0 },
      hooks: { installed: false, count: 0 },
      skills: { installed: false, count: 0 },
    },
    gitflow: {
      configExists: false,
      plansCount: 0,
    },
  };

  const commandsDir = join(claudeDir, 'commands');
  if (await fs.pathExists(commandsDir)) {
    const gitflowExists = await fs.pathExists(join(commandsDir, 'gitflow.md'));
    result.components.commands.installed = gitflowExists;

    const files = await getTemplateFiles(commandsDir);
    result.components.commands.count = files.filter((f) => f.endsWith('.md')).length;
  }

  const agentsDir = join(claudeDir, 'agents');
  if (await fs.pathExists(agentsDir)) {
    const files = await fs.readdir(agentsDir);
    result.components.agents.count = files.filter((f) => f.endsWith('.md')).length;
    result.components.agents.installed = result.components.agents.count > 0;
  }

  const hooksDir = join(claudeDir, 'hooks');
  if (await fs.pathExists(hooksDir)) {
    result.components.hooks.installed = await fs.pathExists(join(hooksDir, 'hooks.json'));
    result.components.hooks.count = result.components.hooks.installed ? 1 : 0;
  }

  const skillsDir = join(claudeDir, 'skills');
  if (await fs.pathExists(skillsDir)) {
    const files = await getTemplateFiles(skillsDir);
    result.components.skills.count = files.filter((f) => f.endsWith('.md')).length;
    result.components.skills.installed = result.components.skills.count > 0;
  }

  const gitflowDir = join(claudeDir, 'gitflow');
  result.gitflow.configExists = await fs.pathExists(join(gitflowDir, 'config.json'));

  const plansDir = join(gitflowDir, 'plans');
  if (await fs.pathExists(plansDir)) {
    const plans = await fs.readdir(plansDir);
    result.gitflow.plansCount = plans.filter((f) => f.endsWith('.md')).length;
  }

  result.installed =
    result.components.commands.installed ||
    result.components.agents.installed ||
    result.components.hooks.installed ||
    result.components.skills.installed;

  return result;
}

export async function listInstalledCommands(options: { global?: boolean } = {}): Promise<string[]> {
  const global = options.global ?? true;
  const claudeDir = getClaudeDir(global);
  const commandsDir = join(claudeDir, 'commands');

  if (!(await fs.pathExists(commandsDir))) {
    return [];
  }

  const files = await getTemplateFiles(commandsDir);
  return files.filter((f) => f.endsWith('.md')).map((f) => f.replace('.md', ''));
}

// ============================================================================
// Plugin Management
// ============================================================================

export interface PluginInfo {
  name: string;
  marketplace: string;
}

export interface PluginInstallResult {
  installed: string[];
  skipped: string[];
  failed: { name: string; error: string }[];
}

/**
 * Check if Claude CLI is available
 */
function isClaudeCliAvailable(): boolean {
  try {
    const result = spawnSync('claude', ['--version'], {
      encoding: 'utf-8',
      shell: true,
      timeout: 5000,
    });
    return result.status === 0;
  } catch {
    return false;
  }
}

/**
 * Get list of installed plugins
 */
export async function getInstalledPlugins(): Promise<string[]> {
  if (!isClaudeCliAvailable()) {
    return [];
  }

  try {
    const result = spawnSync('claude', ['plugin', 'list', '--json'], {
      encoding: 'utf-8',
      shell: true,
      timeout: 10000,
    });

    if (result.status !== 0 || !result.stdout) {
      // Try without --json flag and parse output
      const textResult = spawnSync('claude', ['plugin', 'list'], {
        encoding: 'utf-8',
        shell: true,
        timeout: 10000,
      });

      if (textResult.stdout) {
        // Parse plugin names from text output
        const lines = textResult.stdout.split('\n');
        const plugins: string[] = [];
        for (const line of lines) {
          // Look for plugin names in the output (format varies)
          const match = line.match(/^\s*[-❯•]\s*(\S+)/);
          if (match && match[1] && !match[1].includes(':')) {
            plugins.push(match[1]);
          }
        }
        return plugins;
      }
      return [];
    }

    const data = JSON.parse(result.stdout);
    return Array.isArray(data) ? data.map((p: { name: string }) => p.name) : [];
  } catch {
    return [];
  }
}

/**
 * Check if a specific plugin is installed
 */
export async function isPluginInstalled(pluginName: string): Promise<boolean> {
  const installed = await getInstalledPlugins();
  return installed.some(
    (p) => p === pluginName || p.startsWith(`${pluginName}@`)
  );
}

/**
 * Install a single plugin from marketplace
 */
export async function installPlugin(
  plugin: PluginInfo
): Promise<{ success: boolean; error?: string }> {
  if (!isClaudeCliAvailable()) {
    return { success: false, error: 'Claude CLI not available' };
  }

  const pluginRef = `${plugin.name}@${plugin.marketplace}`;

  try {
    const result = spawnSync('claude', ['plugin', 'install', pluginRef], {
      encoding: 'utf-8',
      shell: true,
      timeout: 60000, // 60 seconds timeout for plugin installation
    });

    if (result.status === 0) {
      return { success: true };
    }

    const errorOutput = result.stderr || result.stdout || 'Unknown error';
    return { success: false, error: errorOutput.trim() };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Installation failed',
    };
  }
}

/**
 * Install all default plugins
 */
export async function installDefaultPlugins(options: {
  force?: boolean;
}): Promise<PluginInstallResult> {
  const result: PluginInstallResult = {
    installed: [],
    skipped: [],
    failed: [],
  };

  if (!isClaudeCliAvailable()) {
    logger.warning('Claude CLI not available - skipping plugin installation');
    for (const plugin of DEFAULT_PLUGINS) {
      result.failed.push({
        name: plugin.name,
        error: 'Claude CLI not available',
      });
    }
    return result;
  }

  // Update marketplace first
  try {
    spawnSync('claude', ['plugin', 'marketplace', 'update'], {
      encoding: 'utf-8',
      shell: true,
      timeout: 30000,
    });
  } catch {
    // Continue even if marketplace update fails
  }

  for (const plugin of DEFAULT_PLUGINS) {
    const isInstalled = await isPluginInstalled(plugin.name);

    if (isInstalled && !options.force) {
      result.skipped.push(plugin.name);
      continue;
    }

    logger.info(`Installing plugin: ${plugin.name}...`);
    const installResult = await installPlugin(plugin);

    if (installResult.success) {
      result.installed.push(plugin.name);
      logger.success(`Installed plugin: ${plugin.name}`);
    } else {
      result.failed.push({ name: plugin.name, error: installResult.error || 'Unknown error' });
      logger.warning(`Failed to install ${plugin.name}: ${installResult.error}`);
    }
  }

  return result;
}

/**
 * Get the list of default plugins
 */
export function getDefaultPlugins(): PluginInfo[] {
  return [...DEFAULT_PLUGINS];
}

// ============================================================================
// Ralph removal (v5.16 — the loop was absorbed by /ba-develop)
// ============================================================================

/**
 * Remove the legacy `.ralph/` directory earlier CLI versions seeded in every
 * project. Ralph's loop no longer exists (`/ba-develop`, `/ba-develop-plan`
 * and `/ba-loop` cover it), so the directory is dead weight. Returns true if
 * something was removed.
 */
export async function purgeRalphProjectArtifacts(
  projectPath: string = process.cwd()
): Promise<boolean> {
  const ralphDir = join(projectPath, '.ralph');
  if (!(await fs.pathExists(ralphDir))) return false;

  try {
    await fs.remove(ralphDir);
    logger.success('Removed legacy .ralph/ directory (Ralph was replaced by /ba-develop)');
    return true;
  } catch (error) {
    const msg = error instanceof Error ? error.message : 'Unknown error';
    logger.warning(`Could not remove legacy .ralph/ directory: ${msg}`);
    return false;
  }
}
